import { useBrand } from '../../hooks/useCompanyProfile';

interface Props {
  /** `md` = desktop sidebar, `sm` = the mobile top bar. */
  size?: 'sm' | 'md';
  /** Colour class for the name; the two bars sit on different surfaces. */
  nameClassName?: string;
  /**
   * Let a long name wrap onto a second line instead of being cut off with an
   * ellipsis. Used by the sidebar drawer, where the theme + close buttons leave
   * roughly 120px for the name — not enough for a three-word company name on
   * one line, so "CLEAN CODE CULTURE" rendered as "CLEAN CODE CU…".
   */
  wrap?: boolean;
}

/**
 * The company mark + name shown at the top of the sidebar and in the mobile top
 * bar: the uploaded square logo when the firm has one, otherwise the first
 * letter of the brand on a solid tile.
 *
 * While the very first load is still resolving (nothing cached yet) this
 * renders a skeleton rather than the product default, so the header never shows
 * "Firmio" and then swaps to the real company name.
 */
export function BrandHeader({
  size = 'md',
  nameClassName = 'text-foreground',
  wrap = false,
}: Props) {
  const { name, logoUrl, isReady } = useBrand();

  const box = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const nameSize = size === 'sm' ? 'text-sm' : 'text-base';
  const initialSize = size === 'sm' ? 'text-xs' : 'text-sm';

  if (!isReady) {
    return (
      <div className="flex items-center gap-2" aria-hidden>
        <div className={`${box} animate-pulse rounded-md bg-muted`} />
        <div className="h-4 w-28 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className={`${box} shrink-0 rounded-md object-cover`}
          // The logo is cropped square on upload, so it never distorts here.
        />
      ) : (
        <div
          className={`flex ${box} shrink-0 items-center justify-center rounded-md bg-primary ${initialSize} font-bold text-primary-foreground`}
        >
          {name.charAt(0).toUpperCase()}
        </div>
      )}
      {/* `line-clamp-2` also sets overflow:hidden, which zeroes this flex item's
          automatic minimum size — without that it would refuse to shrink below
          its longest word and push the buttons off the row. */}
      <span
        className={`font-semibold ${wrap ? 'line-clamp-2 leading-tight' : 'truncate'} ${nameSize} ${nameClassName}`}
      >
        {name}
      </span>
    </div>
  );
}
